import { Link, createFileRoute } from "@tanstack/react-router";
import { Reveal } from "@/components/site/Reveal";
import { CtaPanel, PageHero } from "@/components/site/Sections";
import { services as servicesData } from "@/lib/finendra-data";
import { useCmsList } from "@/hooks/use-cms";
import { serviceDetails } from "@/lib/service-details";

/** Every headline service has a detail page; cards link through by slug. */
const detailRoutes: Record<string, string> = {
  Bookkeeping: "bookkeeping",
  Accounting: "accounting",
  "Payroll Processing": "payroll",
  "Accounts Payable (AP)": "accounts-payable",
  "Accounts Receivable (AR)": "accounts-receivable",
  "Bank & Card Reconciliations": "reconciliations",
  "Month-End & Year-End Close": "month-end-close",
  "Financial Statements & Reporting": "financial-reporting",
  "CFO & Controller Support": "cfo-controller-support",
  "QuickBooks & Xero Management": "quickbooks-xero",
  "Catch-up & Clean-up": "catch-up-cleanup",
  "Finance Process Outsourcing": "finance-process-outsourcing",
};

const detailPath = (slug: string) => `/services/${slug}` as "/services/bookkeeping";

export const Route = createFileRoute("/services/")({
  head: () => ({
    meta: [
      { title: "Outsourced Accounting Services | Finendra" },
      {
        name: "description",
        content:
          "Bookkeeping, payroll, AP/AR, reconciliations, month-end close, financial reporting, CFO support, and QuickBooks & Xero management for businesses worldwide.",
      },
      { property: "og:title", content: "Outsourced Accounting Services | Finendra" },
      {
        property: "og:description",
        content: "Twelve finance and accounting services delivered by a dedicated global team.",
      },
      { property: "og:url", content: "https://finendra.com/services" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Finendra" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:title", content: "Outsourced Accounting Services | Finendra" },
      {
        name: "twitter:description",
        content: "Twelve finance and accounting services delivered by a dedicated global team.",
      },
      { name: "twitter:image:alt", content: "Outsourced Accounting Services | Finendra" },
      { property: "og:image", content: "https://finendra.com/og/og-services.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://finendra.com/og/og-services.png" },
    ],
    links: [{ rel: "canonical", href: "https://finendra.com/services" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Finendra finance and accounting services",
          itemListElement: servicesData.map((s, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Service",
              name: s.title,
              description: s.description,
              serviceType: s.title,
              areaServed: "Worldwide",
              provider: {
                "@type": "Organization",
                name: "Finendra",
                url: "https://finendra.com/",
              },
              url: "https://finendra.com/services",
            },
          })),
        }),
      },
    ],
  }),

  component: ServicesPage,
});

function ServicesPage() {
  const services = useCmsList("service", servicesData);

  return (
    <>
      <PageHero
        eyebrow="Our Services"
        title="End-to-end financial operations, handled."
        body="From daily bookkeeping to controller-level oversight, Finendra runs the finance function your business depends on — accurately, on schedule, and at a predictable monthly cost."
      />

      <section className="px-6 pb-24">
        <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.07}>
              <article className="h-full rounded-3xl border border-brand-navy/5 bg-background p-8 transition-[border-color,box-shadow,transform] duration-200 ease-out hover:-translate-y-1 hover:border-brand-accent/30 hover:shadow-xl motion-reduce:transform-none">
                <div className="mb-6 flex size-12 items-center justify-center rounded-xl bg-brand-accent/10">
                  <span className="font-display text-sm font-bold text-brand-accent">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h2 className="mb-3 text-xl font-bold text-brand-navy">{s.title}</h2>
                <p className="mb-6 text-sm leading-relaxed text-brand-navy/60">{s.description}</p>
                <ul className="space-y-2 text-sm font-medium text-brand-navy/80">
                  {s.points.map((p) => (
                    <li key={p}>• {p}</li>
                  ))}
                </ul>
                {detailRoutes[s.title] ? (
                  <Link
                    to={detailPath(detailRoutes[s.title]!)}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand-accent transition-[gap,color] duration-150 ease-out hover:gap-3"
                  >
                    Deliverables &amp; timeline <span aria-hidden>&rarr;</span>
                  </Link>
                ) : null}
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 pb-16">
        <div className="mx-auto max-w-7xl rounded-[40px] bg-brand-surface p-10 md:p-14">
          <Reveal>
            <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
              Explore the core services in detail
            </h2>
            <p className="mt-4 max-w-2xl text-brand-navy/60">
              Each page lists the exact deliverables, the delivery cadence, and the onboarding
              schedule.
            </p>
          </Reveal>
          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {serviceDetails.map((d, i) => (
              <Reveal key={d.slug} delay={i * 0.05}>
                <Link
                  to={detailPath(d.slug)}
                  className="block h-full rounded-2xl border border-brand-navy/10 bg-background p-6 transition-[border-color,box-shadow,transform] duration-200 ease-out hover:-translate-y-1 hover:border-brand-accent/40 hover:shadow-lg active:scale-[0.98] active:duration-75 motion-reduce:transform-none select-none"
                >
                  <p className="font-bold text-brand-navy">{d.name}</p>
                  <p className="mt-2 text-sm leading-relaxed text-brand-navy/55">{d.intro}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaPanel
        title="Not sure which services you need?"
        body="Send us your current setup and we'll map out exactly what should be outsourced and what should stay in-house."
      />
    </>
  );
}
